import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PageLayout } from '../components/Sidebar';
import { useAuth, useTheme } from '../App';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { ShieldAlert, Beaker, Loader2, Eye, Sparkles, Plus, X, Crown } from 'lucide-react';
import { tl } from '../lib/testingLabApi';
import { toast } from 'sonner';

import SuitesTab from '../components/testing_lab/SuitesTab';
import SessionsTab from '../components/testing_lab/SessionsTab';
import FindingsTab from '../components/testing_lab/FindingsTab';
import QuickFindingsTab from '../components/testing_lab/QuickFindingsTab';
import ChangeOrdersTab from '../components/testing_lab/ChangeOrdersTab';
import FeaturesTab from '../components/testing_lab/FeaturesTab';
import DocsTab from '../components/testing_lab/DocsTab';
import AkroChatPanel from '../components/testing_lab/AkroChatPanel';
import WizardTab from '../components/testing_lab/WizardTab';
import VisualTestLab from '../components/testing_lab/visual/VisualTestLab';

const TestingLabPage = () => {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  const [authStatus, setAuthStatus] = useState({ checked: false, isAdmin: false });
  const [activeTab, setActiveTab] = useState('visual');
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [viewAs, setViewAs] = useState(null);
  const [showViewAs, setShowViewAs] = useState(false);
  const [showAdminMgr, setShowAdminMgr] = useState(false);

  // Honour ?session=...&tab=... deep links (e.g. from "Test This Now" widget)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('session');
    const t = params.get('tab');
    if (s) setActiveSessionId(s);
    if (t && ['sessions', 'suites', 'findings', 'change_orders', 'features', 'docs', 'akro', 'classic_findings', 'wizard', 'visual'].includes(t)) {
      setActiveTab(t);
    }
  }, [location.search]);

  useEffect(() => {
    const check = async () => {
      try {
        const me = await tl.superAdminMe();
        setAuthStatus({ checked: true, isAdmin: me.is_super_admin });
      } catch {
        setAuthStatus({ checked: true, isAdmin: false });
      }
    };
    check();
  }, []);

  if (!authStatus.checked) {
    return (
      <PageLayout title="Testing Lab" theme={theme} setTheme={setTheme}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </PageLayout>
    );
  }

  if (!authStatus.isAdmin) {
    return (
      <PageLayout title="Testing Lab" theme={theme} setTheme={setTheme}>
        <Card className="max-w-2xl mx-auto p-8 mt-12 text-center" data-testid="testing-lab-forbidden">
          <ShieldAlert className="w-12 h-12 mx-auto text-red-500 mb-3" />
          <h2 className="text-lg font-semibold mb-2">Super Admin required</h2>
          <p className="text-sm text-muted-foreground">
            Testing Lab is gated by Super Admin permission. You're signed in as <strong>{user?.email}</strong>.
            Ask an existing Super Admin to grant access.
          </p>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Testing Lab"
      theme={theme}
      setTheme={setTheme}
      actions={
        <div className="flex gap-2">
          {viewAs && (
            <Badge className="bg-amber-500 text-white animate-pulse" data-testid="view-as-badge">
              <Eye className="w-3 h-3 mr-1" /> View as: {viewAs.viewing_as_user_id || viewAs.viewing_as_role}
              <button className="ml-2" onClick={async () => {
                await tl.viewAsStop({ ...viewAs });
                setViewAs(null);
                toast.success('View As stopped');
              }} data-testid="view-as-stop">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          <Button size="sm" variant="outline" onClick={() => setShowViewAs(true)} data-testid="view-as-btn">
            <Eye className="w-3 h-3 mr-1" /> View As
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowAdminMgr(true)} data-testid="admin-mgr-btn">
            <Crown className="w-3 h-3 mr-1" /> Super Admins
          </Button>
        </div>
      }
    >
      <div data-testid="testing-lab-page">
        <div className="flex items-center gap-2 mb-3 px-1">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
            <Beaker className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">
              Live testing · evidence capture · Akro AI · documentation maintenance · code-agent handoff
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-9 w-full max-w-5xl" data-testid="tl-tabs">
            <TabsTrigger value="visual" data-testid="tl-tab-visual">Visual</TabsTrigger>
            <TabsTrigger value="wizard" data-testid="tl-tab-wizard">Wizard</TabsTrigger>
            <TabsTrigger value="findings" data-testid="tl-tab-findings">Findings</TabsTrigger>
            <TabsTrigger value="change_orders" data-testid="tl-tab-change-orders">Change Orders</TabsTrigger>
            <TabsTrigger value="sessions" data-testid="tl-tab-sessions">Sessions</TabsTrigger>
            <TabsTrigger value="suites" data-testid="tl-tab-suites">Suites</TabsTrigger>
            <TabsTrigger value="features" data-testid="tl-tab-features">Features</TabsTrigger>
            <TabsTrigger value="docs" data-testid="tl-tab-docs">Docs</TabsTrigger>
            <TabsTrigger value="akro" data-testid="tl-tab-akro">Akro</TabsTrigger>
          </TabsList>

          <TabsContent value="visual" className="mt-3">
            <VisualTestLab />
          </TabsContent>

          <TabsContent value="wizard" className="mt-3">
            <WizardTab />
          </TabsContent>
          <TabsContent value="findings" className="mt-3">
            <QuickFindingsTab />
          </TabsContent>
          <TabsContent value="change_orders" className="mt-3">
            <ChangeOrdersTab />
          </TabsContent>
          <TabsContent value="sessions" className="mt-3">
            <SessionsTab activeSessionId={activeSessionId} setActiveSessionId={setActiveSessionId} />
          </TabsContent>
          <TabsContent value="suites" className="mt-3">
            <SuitesTab />
          </TabsContent>
          <TabsContent value="classic_findings" className="mt-3">
            <FindingsTab activeSessionId={activeSessionId} />
          </TabsContent>
          <TabsContent value="features" className="mt-3">
            <FeaturesTab />
          </TabsContent>
          <TabsContent value="docs" className="mt-3">
            <DocsTab />
          </TabsContent>
          <TabsContent value="akro" className="mt-3">
            <div className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)]">
              <div className="col-span-12 lg:col-span-7">
                <AkroChatPanel />
              </div>
              <div className="col-span-12 lg:col-span-5">
                <Card className="h-full p-4 text-xs space-y-3">
                  <div>
                    <h4 className="font-semibold mb-1.5 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-violet-500" /> What Akro can do
                    </h4>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• Generate Must / Should / Could behavior + acceptance criteria from a finding</li>
                      <li>• Produce Emergent-ready fix prompts with phased instructions and cleanup notes</li>
                      <li>• Generate risk-audit prompts after a fix to find regressions and downstream effects</li>
                      <li>• Propose Markdown documentation edits scoped to a section, with diffs</li>
                      <li>• Diagnose bugs from screenshots, voice notes, and observations</li>
                      <li>• Identify related sections, code refs, and DB structures</li>
                    </ul>
                  </div>
                  <div className="border-t border-border pt-3">
                    <h4 className="font-semibold mb-1.5">Try asking</h4>
                    <ul className="space-y-1 text-muted-foreground italic">
                      <li>"What test should I run for the Add Income modal?"</li>
                      <li>"Write must/should/could for a flaky calendar sync."</li>
                      <li>"Generate a risk audit for last week's auth fix."</li>
                    </ul>
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <ViewAsDialog open={showViewAs} onClose={() => setShowViewAs(false)}
          activeSessionId={activeSessionId}
          onStarted={(log) => { setViewAs(log); setShowViewAs(false); toast.success('View As started — banner active'); }} />
        <AdminMgrDialog open={showAdminMgr} onClose={() => setShowAdminMgr(false)} />
      </div>
    </PageLayout>
  );
};

const ViewAsDialog = ({ open, onClose, onStarted, activeSessionId }) => {
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const log = await tl.viewAsStart({
        viewing_as_user_id: userId || null,
        viewing_as_role: role || null,
        test_session_id: activeSessionId,
      });
      onStarted(log);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="view-as-dialog">
        <DialogHeader><DialogTitle>View As</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          Start a View-As session. A visible banner will be shown and every action is logged with both your real identity and the impersonated context.
        </p>
        <div className="space-y-2">
          <Input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="user_id (optional)" data-testid="view-as-user-id" />
          <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="role / profile (optional)" data-testid="view-as-role" />
        </div>
        <DialogFooter>
          <Button onClick={start} disabled={(!userId && !role) || busy} data-testid="view-as-start">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AdminMgrDialog = ({ open, onClose }) => {
  const [list, setList] = useState([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const d = await tl.listSuperAdmins(); setList(d.items || []); }
    catch (e) { toast.error(e.message); }
  };
  useEffect(() => { if (open) load(); }, [open]);
  const grant = async () => {
    setBusy(true);
    try {
      await tl.grantSuperAdmin(email);
      setEmail('');
      await load();
      toast.success('Granted');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const revoke = async (em) => {
    if (!window.confirm(`Revoke ${em}?`)) return;
    try {
      await tl.revokeSuperAdmin(em);
      await load();
      toast.success('Revoked');
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-mgr-dialog">
        <DialogHeader><DialogTitle>Super Admin management</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@orka.dev" data-testid="admin-grant-email" />
            <Button onClick={grant} disabled={!email || busy} data-testid="admin-grant-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-3 h-3 mr-1" /> Grant</>}
            </Button>
          </div>
          <div className="space-y-1 mt-3">
            {list.map((a) => (
              <div key={a.email} className="flex items-center justify-between p-2 border border-border rounded text-xs" data-testid={`admin-row-${a.email}`}>
                <div>
                  <div className="font-medium">{a.email}</div>
                  <div className="text-[10px] text-muted-foreground">granted by {a.granted_by} · {new Date(a.granted_at).toLocaleDateString()}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => revoke(a.email)} data-testid={`admin-revoke-${a.email}`}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TestingLabPage;
